// The word a pod wears in a list, in the operator's language.
//
// A pod list is read from across the floor: which of these is actually here,
// which is in someone's hands, which is still somewhere upstream. Odoo's work
// order state answers all of that, but its own words are for the office
// ("pending" is Odoo for "another work order first"), so each state is put
// into floor language here, once, and every list draws the same chip from it.
//
// Three questions, three treatments. Is it here: a pod still upstream is a
// quiet grey tint, a pod standing here is a solid accent chip — the one state
// that asks the operator to do something. Is someone on it: solid green, with
// the play glyph the job screen's resume button wears. Is it finished: the
// green tick tint the Pods screen has always used for a signed-off row. The
// two live states are solid rather than tinted because the default accent
// pack is a muted olive whose tint is all but the neutral grey — tinted,
// "ready" read as "not here yet". Amber stays for a shortage, as on the route
// graph.

import type { WoState } from '@/api/types';
import type { Tone } from '@/components/home/Score';
import type { Translations } from '@/i18n/strings';

export interface PodStateChip {
  tone: Tone;
  solid: boolean;
  label: string;
}

/**
 * What to print for a pod in this state.
 *
 * `started` is the operator's own claim: the pod this device has started. It
 * is a `progress` pod like any other to Odoo, but to the person holding the
 * phone it is "mine", so it gets the word they will look for.
 */
export function podStateChip(state: WoState, t: Translations, started = false): PodStateChip {
  if (started) return { tone: 'pos', solid: true, label: `▶ ${t.startedT}` };
  switch (state) {
    case 'progress':
      return { tone: 'pos', solid: true, label: `▶ ${t.inProgressT}` };
    case 'ready':
      return { tone: 'accent', solid: true, label: t.readyT };
    case 'waiting':
      return { tone: 'warn', solid: false, label: t.waitingForPartsT };
    case 'pending':
      return { tone: 'neutral', solid: false, label: t.notHereYet };
    case 'done':
      return { tone: 'pos', solid: false, label: `✓ ${t.signedOffT}` };
    case 'cancel':
      return { tone: 'neutral', solid: false, label: t.cancelledT };
  }
}
